import { useState, useEffect, useCallback } from 'react';
import { adminService, PhonePoolNumber, PhonePoolStats } from '../../../services/adminService';

export function useAdminPhonePoolPage() {
  const [numbers, setNumbers] = useState<PhonePoolNumber[]>([]);
  const [stats, setStats] = useState<PhonePoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [voiceProvider, setVoiceProvider] = useState<string>('vapi');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Provision form
  const [provisionCount, setProvisionCount] = useState(3);
  const [provisionCountry, setProvisionCountry] = useState('AU');
  const [provisionType, setProvisionType] = useState('mobile');
  const [provisioning, setProvisioning] = useState(false);

  // Twilio test
  const [twilioStatus, setTwilioStatus] = useState<{ configured: boolean; connected: boolean; account_name?: string; error?: string } | null>(null);
  const [testingTwilio, setTestingTwilio] = useState(false);

  // Telnyx sync
  const [syncingTelnyx, setSyncingTelnyx] = useState(false);

  const isGateway = voiceProvider === 'gateway';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [poolNumbers, poolStats, providerInfo] = await Promise.all([
        adminService.getPhonePool(statusFilter || undefined, typeFilter || undefined),
        adminService.getPhonePoolStats(),
        adminService.getVoiceProvider(),
      ]);
      setNumbers(poolNumbers);
      setStats(poolStats);
      setVoiceProvider(providerInfo.provider);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load phone pool data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleProvision = async () => {
    try {
      setProvisioning(true);
      setError(null);
      const result = await adminService.provisionPhoneNumbers(provisionCount, provisionCountry, provisionType);
      setSuccess(`Provisioned ${result.provisioned} number(s) successfully`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Provisioning failed');
    } finally {
      setProvisioning(false);
    }
  };

  const handleRetire = async (poolId: string) => {
    try {
      setError(null);
      await adminService.updatePoolNumber(poolId, 'retire');
      setSuccess('Number retired');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to retire number');
    }
  };

  const handleReactivate = async (poolId: string) => {
    try {
      setError(null);
      await adminService.updatePoolNumber(poolId, 'reactivate');
      setSuccess('Number reactivated');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reactivate number');
    }
  };

  const handleSyncTelnyx = async () => {
    try {
      setSyncingTelnyx(true);
      setError(null);
      const result = await adminService.syncTelnyxNumbers();
      const parts: string[] = [];
      if (result.added > 0) parts.push(`${result.added} number(s) added`);
      if (result.retired > 0) parts.push(`${result.retired} number(s) retired`);
      if (parts.length === 0) parts.push('No changes — pool is up to date');
      if (result.errors?.length > 0) parts.push(`${result.errors.length} error(s)`);
      setSuccess(`Telnyx sync: ${parts.join(', ')}`);
      if (result.errors?.length > 0) {
        setError(`Sync errors: ${result.errors.join('; ')}`);
      }
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Telnyx sync failed');
    } finally {
      setSyncingTelnyx(false);
    }
  };

  const handleTestTwilio = async () => {
    try {
      setTestingTwilio(true);
      setError(null);
      const result = await adminService.testTwilioConnection();
      setTwilioStatus(result);
      if (result.connected) {
        setSuccess(`Twilio connected: ${result.account_name || 'OK'}`);
      } else {
        setError(result.error || 'Twilio connection failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to test Twilio connection');
      setTwilioStatus({ configured: false, connected: false, error: 'Request failed' });
    } finally {
      setTestingTwilio(false);
    }
  };

  return {
    numbers,
    stats,
    loading,
    error,
    success,
    setError,
    setSuccess,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    provisionCount,
    setProvisionCount,
    provisionCountry,
    setProvisionCountry,
    provisionType,
    setProvisionType,
    provisioning,
    handleProvision,
    handleRetire,
    handleReactivate,
    twilioStatus,
    testingTwilio,
    handleTestTwilio,
    syncingTelnyx,
    handleSyncTelnyx,
    loadData,
    isGateway,
  };
}
